import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { CalendarSearch } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import ModalShell from "../modal/ModalShell";
import SlotDayList from "../bookable-slots/SlotDayList";
import { Member } from "../../features/members/models/Member";
import { selectInstructorId, selectIsOrgOwnerOrAdmin } from "../../features/authentication/authenticationSlice";
import { useColleagues } from "../../features/instructors/hooks/useColleagues";
import { useVocabulary } from "../../features/organization/hooks/useVocabulary";
import { BookableSlot, BookableSlots } from "../../features/bookable-slots/models/BookableSlots";
import {
  bookSlotForMember,
  fetchMemberBookableSlots,
} from "../../features/bookable-slots/slice-extensions/bookable-slots-slice-extension";
import { isSlotTaken } from "../../features/bookable-slots/utils/slotApiError";
import { fetchMemberUpcomingSeats } from "../../features/sessions/slice-extensions/occupancy-slice-extension";

interface BookSlotDialogProps {
  member: Member;
}

/**
 * "Book a slot" on the member page: an Owner or Admin picks the instructor (the member's
 * assigned one first), then one of their free slots. A plain Instructor may book any member
 * of the organization, into their own slots only; on a member assigned to a colleague the
 * dialog says so and names that colleague. Staff bookings are confirmed at once.
 */
const BookSlotDialog = ({ member }: BookSlotDialogProps) => {
  const { t } = useTranslation();
  const vocabulary = useVocabulary();
  const dispatch = useAppDispatch();
  const canPickInstructor = useAppSelector(selectIsOrgOwnerOrAdmin);
  const myInstructorId = useAppSelector(selectInstructorId);
  const { activeColleagues, colleagues } = useColleagues();
  const [open, setOpen] = useState(false);
  const [instructorId, setInstructorId] = useState("");
  const [slots, setSlots] = useState<BookableSlots | null>(null);
  const [selected, setSelected] = useState<BookableSlot | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const defaultInstructorId = canPickInstructor ? member.instructorId ?? myInstructorId : myInstructorId;
  const assignedColleague =
    !canPickInstructor && member.instructorId && member.instructorId !== myInstructorId
      ? colleagues.find((c) => c.id === member.instructorId)
      : undefined;

  useEffect(() => {
    if (!open || !instructorId) return;
    let active = true;
    setSlots(null);
    setSelected(null);
    dispatch(fetchMemberBookableSlots({ memberId: member.id, instructorId })).then((result) => {
      if (active && fetchMemberBookableSlots.fulfilled.match(result)) setSlots(result.payload);
    });
    return () => {
      active = false;
    };
  }, [dispatch, open, instructorId, member.id]);

  const openDialog = () => {
    setInstructorId(defaultInstructorId ?? "");
    setError(null);
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setSlots(null);
    setSelected(null);
  };

  const submit = async () => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    const result = await dispatch(
      bookSlotForMember({
        memberId: member.id,
        request: {
          date: selected.date,
          timeLocal: selected.timeLocal,
          unitMinutes: selected.unitMinutes,
          topicId: null,
          instructorId,
        },
      }),
    );
    setBusy(false);
    if (bookSlotForMember.fulfilled.match(result)) {
      toast.success(t("members.bookSlot.success"));
      dispatch(fetchMemberUpcomingSeats(member.id));
      close();
      return;
    }
    setError(isSlotTaken(result.payload) ? t("members.bookSlot.taken") : result.payload?.message || t("members.bookSlot.failed"));
    const refreshed = await dispatch(fetchMemberBookableSlots({ memberId: member.id, instructorId }));
    if (fetchMemberBookableSlots.fulfilled.match(refreshed)) setSlots(refreshed.payload);
    setSelected(null);
  };

  return (
    <>
      <button type="button" className="btn btn-secondary flex items-center gap-1.5" onClick={openDialog}>
        <CalendarSearch size={18} />
        {t("members.detail.bookSlot")}
      </button>
      {open && (
        <ModalShell onClose={close} labelledBy="book-slot-title" maxWidthClassName="max-w-lg">
          <div className="p-4 lg:p-6">
            <h3 id="book-slot-title" className="text-grey-10 dark:text-grey-90">
              {t("members.detail.bookSlot")}
            </h3>
            <p className="mt-1 text-sm text-grey-30 dark:text-grey-70">{`${member.firstName} ${member.lastName}`.trim()}</p>

            <div className="mt-4 space-y-4">
              {assignedColleague && (
                <p className="text-sm text-grey-40 dark:text-grey-80">
                  {t("members.bookSlot.ownSlotsOnly", {
                    ...vocabulary,
                    colleague: `${assignedColleague.firstName} ${assignedColleague.lastName}`.trim(),
                  })}
                </p>
              )}
              {canPickInstructor && (
                <label className="label" htmlFor="book-slot-instructor">
                  <span className="label-text">{t("members.bookSlot.instructor", vocabulary)}</span>
                  <select
                    id="book-slot-instructor"
                    className="input"
                    value={instructorId}
                    onChange={(e) => setInstructorId(e.target.value)}
                  >
                    {activeColleagues.map((c) => (
                      <option key={c.id} value={c.id}>
                        {`${c.firstName} ${c.lastName}`.trim()}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {slots && slots.days.length === 0 && (
                <p className="text-sm text-grey-40 dark:text-grey-80">{t("members.bookSlot.noSlots")}</p>
              )}
              {slots && slots.days.length > 0 && (
                <div className="max-h-80 overflow-y-auto">
                  <SlotDayList days={slots.days} selectedKey={selected?.key ?? null} onSelect={setSelected} disabled={busy} />
                </div>
              )}

              {error && (
                <p role="alert" className="text-sm text-red-700 dark:text-red-400">
                  {error}
                </p>
              )}
            </div>

            <div className="mt-5 flex justify-end gap-3">
              <button type="button" className="btn btn-secondary" onClick={close} disabled={busy}>
                {t("common.cancel")}
              </button>
              <button type="button" className="btn btn-primary" onClick={submit} disabled={busy || !selected}>
                {t("members.bookSlot.submit")}
              </button>
            </div>
          </div>
        </ModalShell>
      )}
    </>
  );
};

export default BookSlotDialog;
